import React from "react";
import styled from "styled-components";
import { tokens } from "../../../../public/tokens";
import { BaseButton } from "../../Button";
import DataTableSearchInput from "./DataTableSearchInput";
import { toolbarButton } from "./inputs.styles";

const StyledBar = styled.div({
    width: "100%",
    alignSelf: "stretch",
    padding: `${tokens.globals["spacing-5"]} ${tokens.globals["spacing-4"]}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: tokens.globals["spacing-4"],
    flexWrap: "wrap",
});

const StyledRow = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    flexWrap: "wrap",
    minWidth: 0,
});

const StyledFilterButton = styled(BaseButton)(({ $active = false }) => ({
    ...toolbarButton,
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${$active ? tokens.colors.brand.blue["500"] : tokens.colors.surfaces["border-divider"]}`,
    cursor: "pointer",
    color: tokens.colors.text.primary,

    "i": { fontSize: tokens.typography.fontSize["lg-14px"] },
    "&:hover:not(:disabled)": { borderColor: tokens.colors.brand.blue["500"] },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
}));

const StyledResetButton = styled(BaseButton)({
    ...toolbarButton,
    background: tokens.colors.neutral.black,
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.neutral.black}`,
    cursor: "pointer",
    color: tokens.colors.neutral.white,

    "i": { fontSize: tokens.typography.fontSize["lg-14px"] },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

const StyledExportGroup = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-3"],
    flexShrink: 0,
});

const StyledExportButton = styled(BaseButton)({
    width: "19px",
    height: "19px",
    padding: 0,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colors.neutral.grey["800"],
    cursor: "pointer",
    borderRadius: tokens.globals["spacing-0-5"],

    "i": { fontSize: "19px", lineHeight: "19px" },
    ".icon-hover": { display: "none" },

    "&:hover:not(:disabled)": {
        color: tokens.colors.neutral.black,

        ".icon-default": { display: "none" },
        ".icon-hover": { display: "inline" },
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

/**
 * Reusable AIE filter/search toolbar: search input, filter dropdown buttons, reset, extra controls, exports.
 * @param {Function} [props.onSearchChange] Called with `(value, event)` on search input change.
 * @param {Array<object>} [props.filters] Filter button definitions; pass `component` to render a custom control.
 * @param {Function} [props.onReset] Called on reset click; button hidden when omitted.
 * @param {Array<object>} [props.exportOptions] Icon-only export buttons, rendered inline after Reset.
 * @param {React.ReactNode} [props.rightContent] Extra control(s) pinned to the far right of the bar.
 * Also: searchValue, searchPlaceholder, searchAriaLabel, resetLabel, disabled, disabledReason, className, children (self-explanatory).
 */
function FilterBar({
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search",
    searchAriaLabel = "Search",
    filters = [],
    onReset,
    resetLabel = "Reset",
    exportOptions = [],
    disabled = false,
    disabledReason,
    className,
    children,
    rightContent,
}) {
    return (
        <StyledBar className={className} aria-busy={disabled || undefined}>
            <StyledRow>
                <DataTableSearchInput
                    value={searchValue}
                    onChange={onSearchChange}
                    placeholder={searchPlaceholder}
                    ariaLabel={searchAriaLabel}
                    disabled={disabled}
                    disabledReason={disabledReason}
                />

                {filters.map((filter, index) => {
                    if (filter.component) {
                        return (
                            <React.Fragment key={filter.key || index}>
                                {filter.component}
                            </React.Fragment>
                        );
                    }

                    const isDisabled = disabled || Boolean(filter.disabled);
                    return (
                        <StyledFilterButton
                            key={filter.key || index}
                            type="button"
                            onClick={filter.onClick}
                            disabled={isDisabled}
                            $active={Boolean(filter.active)}
                            aria-pressed={filter.active === undefined ? undefined : Boolean(filter.active)}
                            title={isDisabled ? (filter.disabledReason || disabledReason) : filter.title}
                        >
                            {filter.icon && <i className={`fal fa-${filter.icon}`} aria-hidden="true" />}
                            <span>{filter.label}</span>
                            <i className="fal fa-chevron-down" aria-hidden="true" />
                        </StyledFilterButton>
                    );
                })}

                {children}

                {onReset && (
                    <StyledResetButton
                        type="button"
                        onClick={onReset}
                        disabled={disabled}
                        title={disabled ? disabledReason : undefined}
                    >
                        <span>{resetLabel}</span>
                        <i className="fal fa-undo" aria-hidden="true" />
                    </StyledResetButton>
                )}

                {exportOptions.length > 0 && (
                    <StyledExportGroup>
                        {exportOptions.map((option, index) => {
                            const isDisabled = disabled || Boolean(option.disabled);
                            return (
                                <StyledExportButton
                                    key={option.key || index}
                                    type="button"
                                    onClick={option.onClick}
                                    disabled={isDisabled}
                                    aria-label={option.ariaLabel || option.label || option.key || "Export"}
                                    title={isDisabled ? (option.disabledReason || disabledReason) : option.title}
                                >
                                    {option.icon && (
                                        <>
                                            <i className={`fal fa-${option.icon} icon-default`} aria-hidden="true" />
                                            <i className={`fas fa-${option.icon} icon-hover`} aria-hidden="true" />
                                        </>
                                    )}
                                </StyledExportButton>
                            );
                        })}
                    </StyledExportGroup>
                )}
            </StyledRow>

            {rightContent && (
                <StyledExportGroup>
                    {rightContent}
                </StyledExportGroup>
            )}
        </StyledBar>
    );
}

FilterBar.displayName = "FilterBar";

export default FilterBar;
